import { useTheme } from '@/app/providers/useTheme';
import { cn } from '@/shared/lib/cn';
import type { ReactNode } from 'react';

export type WordmarkTone = 'onLight' | 'onDark' | 'auto';

export interface WorkerizeWordmarkProps {
  /** Surface the lockup sits on. `auto` (default) follows the active theme. */
  tone?: WordmarkTone;
  /** Mark height in px; the "WORKERIZE" wordmark scales with it. */
  size?: number;
  className?: string;
  /** Accessible/brand text. Latin "Workerize" by default in every locale. */
  title?: string;
}

/**
 * WorkerizeWordmark — the Brand System v2 "iw" mark (rounded W + head dot) plus
 * a live "WORKERIZE" wordmark (rendered as text, not baked into the SVG).
 *
 * The mark is inlined (not the `<img>` asset) with a viewBox cropped tight on
 * the x-axis (x[40,240]) and made VERTICALLY SYMMETRIC about the W's optical
 * centre (y≈212.5): the dot sits at y30, so the box runs y[30,395] — equal
 * headroom above (for the dot tittle) and below. With `items-center` this lands
 * the W on the same line as the wordmark text instead of riding low under it.
 * Colours recolour per surface: Ink on light, Electric Lime on dark. Must
 * render under `<ThemeProvider>` when `tone="auto"`.
 */
export function WorkerizeWordmark({
  tone = 'auto',
  size = 32,
  className,
  title = 'Workerize',
}: WorkerizeWordmarkProps): ReactNode {
  const { theme } = useTheme();
  const resolved = tone === 'auto' ? (theme === 'dark' ? 'onDark' : 'onLight') : tone;
  // Per-surface mark colour: Ink (primary) on light, Electric Lime on dark.
  const markColor = resolved === 'onDark' ? '#C6F432' : '#0B1220';
  const textColor = resolved === 'onDark' ? '#f7f6f2' : '#0b0d1a';
  // W-centered viewBox is 200 wide × 365 tall; width follows that ratio.
  const markWidth = Math.round((size * 200) / 365);
  const SHOW_WORDMARK_TEXT = true;

  return (
    <span
      className={cn('inline-flex items-center', className)}
      style={{ gap: `${Math.round(size * 0.22)}px` }}
    >
      <svg
        width={markWidth}
        height={size}
        viewBox="40 30 200 365"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
        className="shrink-0"
      >
        <path
          d="M60 135L100.631 289.786C101.102 291.58 103.55 291.822 104.363 290.155L140.202 216.685C140.933 215.187 143.067 215.187 143.798 216.685L179.806 290.503C180.598 292.126 182.965 291.952 183.511 290.23L220 175"
          stroke={markColor}
          strokeWidth="40"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="100" cy="59" r="29" fill={markColor} />
      </svg>
      {SHOW_WORDMARK_TEXT ? (
        <span
          className="uppercase leading-none tracking-tight"
          style={{ fontSize: '18px', fontWeight: 500, color: textColor }}
        >
          {title}
        </span>
      ) : null}
    </span>
  );
}

export default WorkerizeWordmark;
